import { useSWR } from '@tiger/swr';
import { Button, Table } from 'antd';
import { observer } from 'mobx-react';
import { FC } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import Card, { CardButtonWrap } from 'components/Card';
import EmptyData from 'components/EmptyData';
import ErrorBox from 'components/ErrorBox';
import ScrollTop from 'components/ScrollTop';
import { TransferMethodEnum } from 'modules/Transfer/type';
import security from 'stores/security';
import API from 'utils/API';
import classlist from 'utils/classlist';
import outerStyles from '../style.scss';
import styles from './style.scss';
import { TransferDetailItem } from './type';

export const statusMap = {
    Processing: {
        className: styles.tagWarning,
        text: __('Processing')
    },
    Withdrawn: {
        className: styles.tagWarning,
        text: __('Withdrawn')
    },
    Received: {
        className: styles.tagSuccess,
        text: __('Received')
    },
    Success: {
        className: styles.tagSuccess,
        text: __('Success')
    },
    Fail: {
        className: styles.tagFailed,
        text: __('Fail')
    }
};

const History: FC = observer(() => {
    const { currentAccountId } = security;
    const navigate = useNavigate();
    const { isLoading, error, mutate, data } = useSWR<TransferDetailItem[]>(
        {
            url: API.CUSTOMER.transfer.transferQuery(),
            params: {
                account_id: currentAccountId
            }
        },
        {
            dataChain: res => res.data
        }
    );

    const column: ColumnsType<TransferDetailItem> = [
        {
            title: __('Date'),
            dataIndex: 'updatedAt',
            render: val => dayjs(val).format('YYYY/MM/DD HH:MM:ss')
        },
        {
            title: __('Delivering Firm'),
            dataIndex: 'broker'
        },
        {
            title: __('Account'),
            dataIndex: 'broker_account'
        },
        {
            title: __('Transfer Type'),
            dataIndex: 'transfer_method',
            render: (transferMethod: TransferMethodEnum) => {
                switch (transferMethod) {
                    case TransferMethodEnum.ACATS:
                        return __('TRANSFER:Full');
                    case TransferMethodEnum.ACATS_PARTIAL:
                        return __('TRANSFER:Partial');
                    default:
                        const _checkNever: never = transferMethod;

                        return '';
                }
            }
        },
        {
            title: __('Status'),
            dataIndex: 'status',
            ellipsis: true,
            render: status => {
                const { className, text } = statusMap[status] || {};

                return <span className={className || ''}>{text}</span>;
            }
        },
        {
            title: __('Operation'),
            dataIndex: '',
            align: 'right',
            render: () => (
                // onRow设置了整体的点击跳转效果, 所以这里不再需要单独设置onClick事件
                <Button className={styles.detailBtn} size="small">
                    {__('Detail')}
                </Button>
            )
        }
    ];

    const render = () => {
        if (error) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        return (
            <div className={styles.table}>
                {!!data?.length || isLoading ? (
                    <Table
                        rowKey="id"
                        columns={column}
                        size="middle"
                        pagination={false}
                        dataSource={data || []}
                        scroll={{ x: true }}
                        loading={isLoading}
                        rowClassName={styles.customerRow}
                        onRow={record => {
                            return {
                                onClick: () => {
                                    navigate(`../${record.id}`);
                                }
                            };
                        }}
                        components={{
                            header: {
                                cell: props => {
                                    return (
                                        <th
                                            {...props}
                                            className={classlist(styles.customerHeader, props.className || '')}>
                                            {props.children}
                                        </th>
                                    );
                                }
                            }
                        }}
                    />
                ) : (
                    <div>
                        <EmptyData description={__('Sorry，no transfer records found')} />
                        <CardButtonWrap className="mt-4">
                            <Link to="/profile/transfer">
                                <Button size="large" type="primary">
                                    {__('Make a Transfer')}
                                </Button>
                            </Link>
                        </CardButtonWrap>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Card form>
            <ScrollTop />
            <h3 className={outerStyles.historyTitle}>{__('Transfer History')}</h3>
            {render()}
        </Card>
    );
});

export default History;
export * from './type';
